import React, { useEffect, useMemo, useState } from 'react';
import { Flex, Text } from '@adobe/react-spectrum';
import { CardStatList } from '../../common/cardStats/CardStatList';
import { StatsSection } from '../../common/cardStats/StatsSection';
import { formatNumber } from '../../common/cardStats/formatters';
import { isValidKindredType } from './mtgCreatureTypes';
import { getPrettyCategoryLabel } from '../../../data/CategoryPrettyDisplayUtils';

/**
 * Colors keyed per top-level archetype. COMBO is red (anchor brand),
 * MIDRANGE green (balanced/creature), CONTROL blue (reactive). Anything
 * else the backend surfaces falls back to gold so it's still visible.
 */
const TOP_ARCHETYPE_COLORS = {
    COMBO: '#cc1a1a',
    MIDRANGE: '#5fa85a',
    CONTROL: '#4a9bd6',
};

const DEFAULT_TOP_COLOR = '#e8c600';

/**
 * Thresholds for surfacing a kindred in the Tribal Signals section.
 * An incidental appearance (e.g. avg 1 bird per deck) isn't a theme —
 * real tribal plays tend to pack either 5+ payoff effects or 10+ cards
 * of the type across the average deck in the bin.
 */
const MIN_TRIBAL_EFFECTS = 5;
const MIN_TRIBAL_CARDS = 10;

const prettyName = getPrettyCategoryLabel;

const topColor = (key) => TOP_ARCHETYPE_COLORS[key] || DEFAULT_TOP_COLOR;

const formatPercent = (value, digits = 0) => {
    if (value == null || Number.isNaN(value)) return '—';
    const parsed = typeof value === 'number' ? value : parseFloat(value);
    if (Number.isNaN(parsed)) return '—';
    return `${parsed.toFixed(digits)}%`;
};


export function ArchetypesTab({ bin, onSelectCard }) {
    const stats = bin?.archetypeStats;

    const majors = stats?.majors || [];
    const labels = stats?.labels || [];
    const avgTopPercentages = stats?.avgTopPercentages || {};
    const topArchetypeCards = stats?.topArchetypeCards || {};
    const subArchetypeCards = stats?.subArchetypeCards || {};
    const kindreds = stats?.kindreds || [];

    const topMixEntries = useMemo(() => {
        return Object.entries(avgTopPercentages)
            .map(([key, pct]) => ({ key, pct: parseFloat(pct) || 0 }))
            .sort((a, b) => b.pct - a.pct);
    }, [avgTopPercentages]);

    // Group sub-archetype card keys ("MAJOR/SUB") by major so we can render
    // chip groups per top-level archetype. Mirrors the "Mana sources / Lands"
    // grouping in the manabase tab.
    const chipGroups = useMemo(() => {
        const grouped = {};
        Object.keys(topArchetypeCards).forEach((major) => {
            if (!grouped[major]) grouped[major] = { major, subs: [] };
        });
        Object.keys(subArchetypeCards).forEach((compoundKey) => {
            const [major, sub] = compoundKey.split('/');
            if (!major || !sub) return;
            if (!grouped[major]) grouped[major] = { major, subs: [] };
            grouped[major].subs.push({ sub, compoundKey });
        });
        // Sort subs by card count desc so the biggest sub-archetype leads
        Object.values(grouped).forEach((group) => {
            group.subs.sort((a, b) => {
                const aCount = (subArchetypeCards[a.compoundKey] || []).length;
                const bCount = (subArchetypeCards[b.compoundKey] || []).length;
                return bCount - aCount;
            });
        });
        // Sort groups by majors[] ordering (count desc) — falling back to
        // avgTopPercentages when a major appears only in cards, not majors
        const orderIndex = (key) => {
            const idx = majors.findIndex((m) => m.key === key);
            if (idx >= 0) return idx;
            const pct = avgTopPercentages[key] || 0;
            return 1000 - pct;
        };
        return Object.values(grouped).sort((a, b) => orderIndex(a.major) - orderIndex(b.major));
    }, [topArchetypeCards, subArchetypeCards, majors, avgTopPercentages]);

    // Initial chip selection: first label ("MAJOR/MINOR") if the combo has
    // a subArchetypeCards entry; otherwise the top major's "all" view.
    const defaultChip = useMemo(() => {
        const firstLabel = labels[0]?.key;
        if (firstLabel && subArchetypeCards[firstLabel]) {
            const [major, sub] = firstLabel.split('/');
            return { kind: 'sub', major, sub, compoundKey: firstLabel };
        }
        const firstMajor = chipGroups[0]?.major;
        if (firstMajor && topArchetypeCards[firstMajor]?.length) {
            return { kind: 'top', major: firstMajor };
        }
        return null;
    }, [labels, subArchetypeCards, chipGroups, topArchetypeCards]);

    const [activeChip, setActiveChip] = useState(defaultChip);

    // Reset the selection if bin data swaps and the current chip no longer
    // exists in the new bin (e.g. user changes bracket bucket).
    useEffect(() => {
        setActiveChip(defaultChip);
    }, [defaultChip]);

    // Defensive allowlist filter — backend leaks card-text fragments like
    // "while"/"searching" into kindred types. Drop once upstream is fixed.
    // Floor: show tribal signal only when avg effect references per deck
    // is meaningful (≥5 payoffs, ≈real tribal archetype) OR there are
    // ≥10 cards of that type per deck (big tribal body count even when
    // the deck has few tribal payoff cards).
    const tribalKindreds = useMemo(
        () =>
            kindreds.filter(
                (k) =>
                    isValidKindredType(k?.type) &&
                    ((k?.avgEffectCount || 0) >= MIN_TRIBAL_EFFECTS ||
                        (k?.avgTypeCount || 0) >= MIN_TRIBAL_CARDS)
            ),
        [kindreds]
    );

    if (!stats) {
        return (
            <Text UNSAFE_className="commanderCardStatsEmpty">
                Archetype stats aren't available for this bucket yet — they'll populate after the next rebuild.
            </Text>
        );
    }

    const activeCards = (() => {
        if (!activeChip) return [];
        if (activeChip.kind === 'top') return topArchetypeCards[activeChip.major] || [];
        if (activeChip.kind === 'sub') return subArchetypeCards[activeChip.compoundKey] || [];
        return [];
    })();

    const activeChipTitle = (() => {
        if (!activeChip) return '';
        if (activeChip.kind === 'top') return `All ${prettyName(activeChip.major)}`;
        return `${prettyName(activeChip.major)} / ${prettyName(activeChip.sub)}`;
    })();

    return (
        <Flex direction="column" rowGap={24} marginTop={10}>
            {/* ----- TOP-LEVEL ARCHETYPE MIX ----- */}
            {topMixEntries.length > 0 && (
                <StatsSection
                    title="Top-level archetype mix"
                    subtitle="Avg % of archetype signal per top-level category across all decks"
                >
                    <div className="archetypeMixBar">
                        {topMixEntries.map((entry) => (
                            <div
                                key={entry.key}
                                className="archetypeMixSegment"
                                style={{
                                    width: `${Math.max(entry.pct, 0)}%`,
                                    background: topColor(entry.key),
                                }}
                                title={`${prettyName(entry.key)} ${formatPercent(entry.pct, 1)}`}
                            />
                        ))}
                    </div>
                    <div className="archetypeMixLegend">
                        {topMixEntries.map((entry) => (
                            <div key={entry.key} className="archetypeMixLegendItem">
                                <span
                                    className="archetypeMixLegendSwatch"
                                    style={{ background: topColor(entry.key) }}
                                />
                                <span className="archetypeMixLegendLabel">{prettyName(entry.key)}</span>
                                <span className="archetypeMixLegendValue">{formatPercent(entry.pct, 1)}</span>
                            </div>
                        ))}
                    </div>
                </StatsSection>
            )}

            {/* ----- LABEL DISTRIBUTION ----- */}
            {labels.length > 0 && (
                <StatsSection
                    title="Most common builds"
                    subtitle="Dominant archetype classification across decks in this bucket"
                >
                    <div className="archetypeLabelList">
                        {labels.slice(0, 8).map((entry) => {
                            const [major] = (entry.key || '').split('/');
                            const pct = Math.round((entry.rate || 0) * 100);
                            return (
                                <div key={entry.key} className="archetypeLabelRow">
                                    <span className="archetypeLabelName">
                                        {(entry.key || '')
                                            .split('/')
                                            .map(prettyName)
                                            .join(' / ')}
                                    </span>
                                    <div className="archetypeLabelBarTrack">
                                        <div
                                            className="archetypeLabelBarFill"
                                            style={{
                                                width: `${Math.max(pct, 1)}%`,
                                                background: topColor(major),
                                            }}
                                        />
                                    </div>
                                    <span className="archetypeLabelPct">{`${pct}%`}</span>
                                    <span className="archetypeLabelCount">
                                        {`${entry.count}/${stats.deckCount}`}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </StatsSection>
            )}

            {/* ----- TRIBAL SIGNALS (kindreds with effect density) ----- */}
            {tribalKindreds.length > 0 && (
                <StatsSection
                    title="Tribal signals"
                    subtitle="Creature types with effect density beyond incidental presence"
                >
                    <div className="archetypeKindredList">
                        {tribalKindreds.map((kindred) => {
                            const pct = Math.round((kindred.deckRate || 0) * 100);
                            const name = prettyName(kindred.type);
                            const rowTitle =
                                `${name} — tribal signal across ${stats.deckCount} decks in this bucket.\n` +
                                `• ${pct}% of decks reference ${name.toLowerCase()} in some way\n` +
                                `• Avg ${formatNumber(kindred.avgEffectCount, 2)} effects referencing ${name.toLowerCase()} per deck\n` +
                                `• Avg ${formatNumber(kindred.avgTypeCount, 2)} ${name.toLowerCase()} cards per deck`;
                            return (
                                <div
                                    key={kindred.type}
                                    className="archetypeKindredRow"
                                    title={rowTitle}
                                >
                                    <span className="archetypeKindredType">{name}</span>
                                    <div className="archetypeKindredBarTrack">
                                        <div
                                            className="archetypeKindredBarFill"
                                            style={{ width: `${Math.max(pct, 1)}%` }}
                                        />
                                    </div>
                                    <span className="archetypeKindredPct">{`${pct}%`}</span>
                                    <span
                                        className="archetypeKindredMetric"
                                        title={`Avg count of effects referencing ${name.toLowerCase()} per deck (${stats.deckCount} decks)`}
                                    >
                                        <span className="archetypeKindredMetricLabel">avg effects</span>
                                        <span className="archetypeKindredMetricValue">
                                            {formatNumber(kindred.avgEffectCount, 1)}
                                        </span>
                                    </span>
                                    <span
                                        className="archetypeKindredMetric"
                                        title={`Avg count of ${name.toLowerCase()} cards per deck (${stats.deckCount} decks)`}
                                    >
                                        <span className="archetypeKindredMetricLabel">avg cards</span>
                                        <span className="archetypeKindredMetricValue">
                                            {formatNumber(kindred.avgTypeCount, 1)}
                                        </span>
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </StatsSection>
            )}

            {/* ----- ARCHETYPE CARDS BROWSER ----- */}
            {chipGroups.length > 0 && (
                <StatsSection
                    title="Archetype cards"
                    subtitle="Pick a lean to see the cards defining that archetype in this bucket"
                >
                    <div className="archetypeChipGroups">
                        {chipGroups.map((group) => {
                            const majorCardCount = (topArchetypeCards[group.major] || []).length;
                            const color = topColor(group.major);
                            return (
                                <div key={group.major} className="archetypeChipGroup">
                                    <span
                                        className="archetypeChipGroupLabel"
                                        style={{ color }}
                                    >
                                        {prettyName(group.major)}
                                    </span>
                                    <div className="archetypeChipRow">
                                        {majorCardCount > 0 && (
                                            <ArchetypeChip
                                                label={`All ${prettyName(group.major)}`}
                                                count={majorCardCount}
                                                color={color}
                                                isActive={
                                                    activeChip?.kind === 'top' &&
                                                    activeChip?.major === group.major
                                                }
                                                onClick={() =>
                                                    setActiveChip({
                                                        kind: 'top',
                                                        major: group.major,
                                                    })
                                                }
                                            />
                                        )}
                                        {group.subs.map(({ sub, compoundKey }) => {
                                            const count = (subArchetypeCards[compoundKey] || []).length;
                                            if (count === 0) return null;
                                            return (
                                                <ArchetypeChip
                                                    key={compoundKey}
                                                    label={prettyName(sub)}
                                                    count={count}
                                                    color={color}
                                                    isActive={
                                                        activeChip?.kind === 'sub' &&
                                                        activeChip?.compoundKey === compoundKey
                                                    }
                                                    onClick={() =>
                                                        setActiveChip({
                                                            kind: 'sub',
                                                            major: group.major,
                                                            sub,
                                                            compoundKey,
                                                        })
                                                    }
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {activeChip && (
                        <Flex direction="column" rowGap={6} marginTop={12}>
                            <span className="archetypeActiveTitle">{activeChipTitle}</span>
                            <CardStatList
                                cards={activeCards}
                                deckCount={bin.deckCount}
                                onSelectCard={onSelectCard}
                                resetKey={`archetype:${activeChip.compoundKey || activeChip.major}:${bin?.binKey}`}
                                emptyMessageUnfiltered="No cards recorded for this archetype lean."
                            />
                        </Flex>
                    )}
                </StatsSection>
            )}
        </Flex>
    );
}

function ArchetypeChip({ label, count, color, isActive, onClick }) {
    return (
        <button
            type="button"
            className={`archetypeChip ${isActive ? 'is-active' : ''}`}
            onClick={onClick}
            style={isActive ? { borderLeftColor: color, color } : { borderLeftColor: color }}
        >
            <span className="archetypeChipLabel">{label}</span>
            <span className="archetypeChipCount">{count}</span>
        </button>
    );
}
